import type { SupervisorAgent, Tool } from "@databricks/sdk-supervisoragents/v1";
import type { SupervisorAgentsClient } from "@databricks/sdk-supervisoragents/v1";
import type {
  DatabricksAgentEndpointClient,
  DatabricksAgentEndpointInvocation,
  DatabricksAgentEndpointResponse,
} from "./agent-endpoints.js";

export interface DatabricksSupervisorAgentsOptions {
  /** Supervisor Agents is a Databricks Beta and must be enabled deliberately. */
  acknowledgeBeta: true;
}

export interface DatabricksSupervisorAgentListOptions {
  pageSize?: number;
  maxPages?: number;
}

/** Guarded composition boundary over the official generated Supervisor Agents SDK. */
export class DatabricksSupervisorAgents {
  /** Official client for explicit create, update, tool, example, and delete lifecycles. */
  readonly sdk: SupervisorAgentsClient;

  constructor(
    sdk: SupervisorAgentsClient,
    private readonly endpoints: DatabricksAgentEndpointClient,
    options: DatabricksSupervisorAgentsOptions,
  ) {
    if (options.acknowledgeBeta !== true) {
      throw new Error("databricks supervisor agents: acknowledgeBeta must be true.");
    }
    this.sdk = sdk;
  }

  /** Bounded, token-loop-safe discovery across the current workspace. */
  async list(options: DatabricksSupervisorAgentListOptions = {}): Promise<SupervisorAgent[]> {
    const pageSize = boundedInteger(options.pageSize ?? 100, "pageSize", 1, 100);
    const maxPages = boundedInteger(options.maxPages ?? 100, "maxPages", 1, 1000);
    const agents = new Map<string, SupervisorAgent>();
    const seenTokens = new Set<string>();
    let pageToken: string | undefined;
    for (let page = 0; page < maxPages; page += 1) {
      const result = await this.sdk.listSupervisorAgents({
        pageSize,
        ...(pageToken ? { pageToken } : {}),
      });
      for (const agent of result.supervisorAgents ?? []) {
        const key = agent.name ?? agent.supervisorAgentId;
        if (key) agents.set(key, agent);
      }
      const next = result.nextPageToken?.trim();
      if (!next) return [...agents.values()];
      if (seenTokens.has(next)) {
        throw new Error(
          `databricks supervisor agents pagination repeated token after page ${page + 1}.`,
        );
      }
      seenTokens.add(next);
      pageToken = next;
    }
    throw new Error(`databricks supervisor agents pagination exceeded ${maxPages} pages.`);
  }

  async listTools(
    supervisorAgent: string,
    options: DatabricksSupervisorAgentListOptions = {},
  ): Promise<Tool[]> {
    const parent = supervisorResourceName(supervisorAgent);
    const pageSize = boundedInteger(options.pageSize ?? 100, "pageSize", 1, 100);
    const maxPages = boundedInteger(options.maxPages ?? 100, "maxPages", 1, 1000);
    const tools = new Map<string, Tool>();
    const seenTokens = new Set<string>();
    let pageToken: string | undefined;
    for (let page = 0; page < maxPages; page += 1) {
      const result = await this.sdk.listTools({
        parent,
        pageSize,
        ...(pageToken ? { pageToken } : {}),
      });
      for (const tool of result.tools ?? []) {
        const key = tool.name ?? tool.toolId;
        if (key) tools.set(key, tool);
      }
      const next = result.nextPageToken?.trim();
      if (!next) return [...tools.values()];
      if (seenTokens.has(next)) {
        throw new Error(
          `databricks supervisor tools pagination repeated token after page ${page + 1}.`,
        );
      }
      seenTokens.add(next);
      pageToken = next;
    }
    throw new Error(`databricks supervisor tools pagination exceeded ${maxPages} pages.`);
  }

  /** Resolve the Databricks-owned endpoint at call time, then invoke its Responses contract. */
  async invoke(
    supervisorAgent: string,
    invocation: DatabricksAgentEndpointInvocation,
  ): Promise<DatabricksAgentEndpointResponse> {
    const agent = await this.sdk.getSupervisorAgent({
      name: supervisorResourceName(supervisorAgent),
    });
    const endpointName = agent.endpointName?.trim();
    if (!endpointName) {
      throw new Error(`Databricks Supervisor Agent ${supervisorAgent} has no queryable endpoint.`);
    }
    return this.endpoints.invoke(endpointName, invocation);
  }
}

export function databricksSupervisorAgents(
  sdk: SupervisorAgentsClient,
  endpoints: DatabricksAgentEndpointClient,
  options: DatabricksSupervisorAgentsOptions,
): DatabricksSupervisorAgents {
  return new DatabricksSupervisorAgents(sdk, endpoints, options);
}

function supervisorResourceName(value: string): string {
  const normalized = value.trim();
  if (!normalized) throw new Error("supervisorAgent cannot be empty.");
  if (normalized.startsWith("supervisor-agents/")) {
    if (!/^supervisor-agents\/[A-Za-z0-9-]+$/u.test(normalized)) {
      throw new Error("supervisorAgent must be an id or supervisor-agents/<id> resource name.");
    }
    return normalized;
  }
  if (!/^[A-Za-z0-9-]+$/u.test(normalized)) {
    throw new Error("supervisorAgent must be an id or supervisor-agents/<id> resource name.");
  }
  return `supervisor-agents/${normalized}`;
}

function boundedInteger(value: number, name: string, min: number, max: number): number {
  if (!Number.isSafeInteger(value) || value < min || value > max) {
    throw new Error(`${name} must be an integer between ${min} and ${max}.`);
  }
  return value;
}
